const ASSISTANT_SCREEN_LABELS = {
  home: 'Home',
  setup: 'Setup Guide',
  sources: 'Sources',
  products: 'Products',
  orders: 'Orders',
  analytics: 'Analytics',
  export: 'Export',
  support: 'Support',
  settings: 'Settings',
};

const ASSISTANT_ALLOWED_SCREENS_CLIENT = new Set(Object.keys(ASSISTANT_SCREEN_LABELS));
const ASSISTANT_ALLOWED_FOCUS_CLIENT = new Set([
  'connect-source',
  'manual-order',
  'new-product',
  'map-products',
  'new-rule',
  'send-batch',
  'view-activity',
]);
const ASSISTANT_ALLOWED_DOC_HOST = 'omnifill.net';
const ASSISTANT_ONBOARDING_FOCUS_KEY = 'omnifill_onboarding_focus';

const ASSISTANT_STARTER_PROMPTS = [
  'Help me build my first fulfillment workflow',
  'I need to route Kickstarter orders to a 3PL',
  'Show me how to make a warehouse CSV',
  'What should I do before turning on auto-send?',
];

const INITIAL_ASSISTANT_MESSAGE = {
  role: 'assistant',
  text: 'Tell me what kind of order workflow you want to build. I can guide you through Sources, Products, Orders, and Export, then highlight the next action in the dashboard.',
  actions: [
    { type: 'navigate', label: 'Sources', screen: 'sources', focus: 'connect-source' },
    { type: 'navigate', label: 'Products', screen: 'products', focus: 'new-product' },
    { type: 'navigate', label: 'Export', screen: 'export', focus: 'new-rule' },
  ],
  suggestions: ASSISTANT_STARTER_PROMPTS.slice(0, 3),
};

const assistantEscapeRegExp = value => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const assistantActionKey = action => [
  action?.type || '',
  action?.screen || '',
  action?.focus || '',
  action?.url || '',
  action?.label || '',
].join(':');

const isAllowedAssistantDocUrlClient = url => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' && parsed.hostname === ASSISTANT_ALLOWED_DOC_HOST && (
      parsed.pathname === '/docs'
      || parsed.pathname === '/resources/'
      || parsed.pathname.startsWith('/resources/')
    );
  } catch {
    return false;
  }
};

const normalizeAssistantActionClient = action => {
  const type = ['navigate', 'highlight', 'docs'].includes(action?.type) ? action.type : 'navigate';
  const screen = ASSISTANT_ALLOWED_SCREENS_CLIENT.has(action?.screen) ? action.screen : null;
  const focus = ASSISTANT_ALLOWED_FOCUS_CLIENT.has(action?.focus) ? action.focus : null;
  const url = action?.url && isAllowedAssistantDocUrlClient(action.url) ? action.url : null;
  const label = String(action?.label || ASSISTANT_SCREEN_LABELS[screen] || (url ? 'Open docs' : 'Open')).trim().slice(0, 48);
  if ((type === 'navigate' || type === 'highlight') && !screen) return null;
  if (type === 'docs' && !url) return null;
  return { type, screen, focus, url, label };
};

const normalizeAssistantMessage = (message, fallbackRole = 'assistant') => ({
  role: message?.role === 'user' ? 'user' : fallbackRole,
  text: String(message?.text || message?.reply || '').trim(),
  actions: Array.isArray(message?.actions) ? message.actions.map(normalizeAssistantActionClient).filter(Boolean) : [],
  suggestions: Array.isArray(message?.suggestions) ? message.suggestions.map(item => String(item || '').trim()).filter(Boolean).slice(0, 4) : [],
});

const renderAssistantText = (text, actions, onAction) => {
  const normalizedActions = actions.map(normalizeAssistantActionClient).filter(Boolean);
  const byLabel = new Map();
  for (const action of normalizedActions) {
    const labels = [action.label, action.screen ? ASSISTANT_SCREEN_LABELS[action.screen] : '']
      .map(label => String(label || '').trim())
      .filter(label => label.length >= 3);
    for (const label of labels) {
      if (!byLabel.has(label.toLowerCase())) byLabel.set(label.toLowerCase(), action);
    }
  }
  const labels = [...byLabel.keys()].sort((a, b) => b.length - a.length);
  if (!labels.length) return text;
  const pattern = new RegExp(`(${labels.map(assistantEscapeRegExp).join('|')})`, 'gi');
  return String(text || '').split(pattern).map((part, index) => {
    const action = byLabel.get(part.toLowerCase());
    if (!action) return part;
    return (
      <button key={`${part}-${index}`} type="button" className="assistant-inline-action" onClick={() => onAction(action)}>
        {part}
      </button>
    );
  });
};

const AssistantDrawer = ({ open, onClose, screen, onNavigate, dashboardContext }) => {
  const [messages, setMessages] = useState([INITIAL_ASSISTANT_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = event => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [open, messages.length, loading]);

  const runAction = action => {
    const normalized = normalizeAssistantActionClient(action);
    if (!normalized) return;
    if (normalized.type === 'docs' && normalized.url) {
      window.open(normalized.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!ASSISTANT_ALLOWED_SCREENS_CLIENT.has(normalized.screen)) return;
    try {
      if (normalized.focus) {
        sessionStorage.setItem(ASSISTANT_ONBOARDING_FOCUS_KEY, JSON.stringify({
          screen: normalized.screen,
          focus: normalized.focus,
          createdAt: Date.now(),
        }));
      }
    } catch {}
    onNavigate?.(normalized.screen);
  };

  const submitMessage = async (overrideText = '') => {
    const text = String(overrideText || input || '').trim();
    if (!text || loading) return;
    const userMessage = { role: 'user', text };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);
    setInput('');
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await api('POST', '/api/assistant/chat', {
        message: text,
        currentScreen: screen,
        history: nextMessages
          .filter(message => message.role === 'user' || message.role === 'assistant')
          .slice(-8)
          .map(message => ({ role: message.role, text: message.text })),
        dashboardContext,
      });
      if (!ok) {
        throw new Error(data?.error || 'The assistant could not answer right now.');
      }
      const assistantMessage = normalizeAssistantMessage(data, 'assistant');
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      const message = err?.message || 'The assistant could not answer right now.';
      setError(message);
      setMessages(prev => [...prev, { role: 'assistant', text: message, actions: [{ type:'navigate', label:'Support', screen:'support' }], suggestions: [] }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = event => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    submitMessage();
  };

  if (!open) return null;

  return (
    <div className="assistant-layer" aria-live="polite">
      <div className="assistant-scrim" onClick={onClose}></div>
      <aside className="assistant-drawer" aria-label="Omnifill AI assistant">
        <header className="assistant-header">
          <div className="assistant-title-mark">
            <Sparkles size={18} strokeWidth={2}/>
          </div>
          <div style={{ minWidth:0, flex:1 }}>
            <h2>Omnifill Assistant</h2>
            <p>Dashboard workflow guide.</p>
          </div>
          <button type="button" className="assistant-icon-button" onClick={onClose} title="Close assistant" aria-label="Close assistant">
            <X size={16} strokeWidth={2}/>
          </button>
        </header>

        <div ref={messagesRef} className="assistant-messages">
          {messages.map((message, index) => {
            const actions = (message.actions || []).map(normalizeAssistantActionClient).filter(Boolean);
            const suggestions = message.suggestions || [];
            return (
              <div key={index} className={`assistant-message-row assistant-message-row-${message.role}`}>
                <div className="assistant-message-bubble">
                  <div className="assistant-message-copy">
                    {renderAssistantText(message.text, actions, runAction)}
                  </div>
                  {actions.length > 0 && (
                    <div className="assistant-action-list">
                      {actions.map(action => (
                        <button key={assistantActionKey(action)} type="button" className="assistant-action-chip" onClick={() => runAction(action)}>
                          {action.type === 'docs' ? <ExternalLink size={12} strokeWidth={2}/> : <ArrowRight size={12} strokeWidth={2}/>}
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {message.role === 'assistant' && suggestions.length > 0 && !loading && (
                    <div className="assistant-suggestion-list">
                      {suggestions.map(suggestion => (
                        <button key={suggestion} type="button" onClick={() => submitMessage(suggestion)}>
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {loading && (
            <div className="assistant-message-row assistant-message-row-assistant">
              <div className="assistant-message-bubble assistant-loading-bubble">
                <span></span><span></span><span></span>
              </div>
            </div>
          )}
        </div>

        {error && <div className="assistant-error">{error}</div>}

        <div className="assistant-composer">
          <div className="assistant-starter-row">
            {ASSISTANT_STARTER_PROMPTS.slice(0, 2).map(prompt => (
              <button key={prompt} type="button" onClick={() => submitMessage(prompt)} disabled={loading}>
                <Sparkles size={12} strokeWidth={2}/>{prompt}
              </button>
            ))}
          </div>
          <div className="assistant-input-wrap">
            <textarea
              value={input}
              onChange={event => setInput(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe the workflow you want to build..."
              rows={3}
              maxLength={1600}
              disabled={loading}
            />
            <button type="button" onClick={() => submitMessage()} disabled={!input.trim() || loading} title="Send message" aria-label="Send message">
              <Send size={15} strokeWidth={2}/>
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
};
